import { NutrientKey } from '../../../../services/feedAnalysisService';

// Leitura do "Relatório de Ensaio" do laboratório (Aurora Xaxim): o pdf tem
// camada de texto, então o pdfjs devolve cada trecho com a posição (x, y).
// Aqui as posições viram linhas e as linhas viram os campos do formulário.
// Puro (sem pdfjs) para ser testado com o texto de um laudo real.

export interface PdfTextItem {
  str: string;
  x: number;
  y: number;
}

export interface PdfPage {
  width: number;
  items: PdfTextItem[];
}

interface LineItem {
  text: string;
  x: number;
}

export interface LabReportResult {
  name: string; // como veio no laudo
  key: NutrientKey | null; // null = análise que o módulo não conhece
  value: number | null;
  unit: string;
}

export interface LabReport {
  sample_code: string | null;
  item_name: string | null;
  lot: string | null;
  supplier: string | null;
  manufactured_on: string | null; // AAAA-MM-DD
  collected_on: string | null;
  received_on: string | null;
  released_at: string | null; // AAAA-MM-DDTHH:MM
  laboratory: string | null;
  results: LabReportResult[];
  values: Partial<Record<NutrientKey, number>>;
  // campos esperados que não foram achados: a revisão destaca
  missing: ('sample_code' | 'item_name' | 'collected_on' | 'results')[];
}

// trechos com diferença de altura até isto ficam na mesma linha
const LINE_TOLERANCE = 2;

export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// de cima para baixo, da esquerda para a direita
export function groupLines(page: PdfPage): LineItem[][] {
  const items = page.items
    .filter(item => item.str.trim())
    .sort((a, b) => b.y - a.y || a.x - b.x);
  const lines: { y: number; items: LineItem[] }[] = [];

  items.forEach(item => {
    const line = lines.find(
      candidate => Math.abs(candidate.y - item.y) <= LINE_TOLERANCE,
    );
    const entry = { text: item.str.trim(), x: item.x };

    if (line) line.items.push(entry);
    else lines.push({ y: item.y, items: [entry] });
  });

  return lines.map(line => line.items.sort((a, b) => a.x - b.x));
}

const lineText = (line: LineItem[]) => line.map(item => item.text).join(' ');

// "17,58" / "1.234,5" -> número; "-" ou vazio -> null
export function parseBrazilianNumber(value: string): number | null {
  const match = value.replace(/\s/g, '').match(/^-?[\d.]*,?\d+$/);
  if (!match) return null;
  const number = Number(
    value.replace(/\s/g, '').replace(/\./g, '').replace(',', '.'),
  );
  return Number.isFinite(number) ? number : null;
}

function parseDate(value: string | undefined): string | null {
  const match = (value || '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
}

function parseDateTime(value: string | undefined): string | null {
  const date = parseDate(value);
  const time = (value || '').match(/(\d{2}):(\d{2})/);
  if (!date) return null;
  return time ? `${date}T${time[1]}:${time[2]}` : `${date}T00:00`;
}

const blankToNull = (value: string | undefined) => {
  const text = (value || '').trim();
  return text && text !== '-' ? text : null;
};

// nome da análise no laudo -> nutriente do módulo
const ANALYSIS_KEYS: [RegExp, NutrientKey][] = [
  [/^umidade/, 'moisture'],
  [/^proteina bruta/, 'crude_protein'],
  [/^extrato etereo/, 'ether_extract'],
  [/^(materia mineral|cinzas|residuo mineral)/, 'mineral_matter'],
  [/^calcio/, 'calcium'],
  [/^fosforo/, 'phosphorus'],
  [/^fibra bruta/, 'crude_fiber'],
  [/(diametro geometrico medio|\bdgm\b)/, 'dgm'],
  [/(desvio padrao geometrico|\bdpg\b)/, 'dpg'],
];

export function analysisKey(name: string): NutrientKey | null {
  const normalized = normalizeText(name);
  const found = ANALYSIS_KEYS.find(([pattern]) => pattern.test(normalized));
  return found ? found[1] : null;
}

// rótulos em duas colunas: o valor vem na linha de baixo, alinhado ao rótulo
const COLUMN_TOLERANCE = 20;

const FIELD_LABELS = {
  item: 'item',
  lot: 'numero do lote',
  supplier: 'fornecedor',
  manufactured: 'data de fabricacao',
  collected: 'data de coleta',
  received: 'data de recebimento',
  released: 'data de liberacao',
};

type FieldName = keyof typeof FIELD_LABELS;

function readLabeledFields(
  lines: LineItem[][],
): Partial<Record<FieldName, string>> {
  const fields: Partial<Record<FieldName, string>> = {};
  const labels = Object.entries(FIELD_LABELS) as [FieldName, string][];

  lines.forEach((line, index) => {
    const next = lines[index + 1] || [];

    line.forEach(item => {
      const label = labels.find(
        ([, text]) => normalizeText(item.text) === text,
      );
      if (!label || fields[label[0]] !== undefined) return;

      const value = next
        .filter(candidate => Math.abs(candidate.x - item.x) <= COLUMN_TOLERANCE)
        .map(candidate => candidate.text)
        .join(' ');

      fields[label[0]] = value;
    });
  });

  return fields;
}

// "1  Cálcio  0,41 %" | "8  Diâmetro ... (DGM)  504,00 µm" | "9  ...  1,86"
const RESULT_LINE =
  /^(\d{1,2})\s+(.+?)\s+(-?[\d.]*,?\d+)\s*(%|[µμu]m|mg\/kg|ppm|g\/kg)?$/i;

function readResults(lines: LineItem[][]): LabReportResult[] {
  const start = lines.findIndex(line =>
    normalizeText(lineText(line)).startsWith('resultados analiticos'),
  );
  if (start < 0) return [];

  const results: LabReportResult[] = [];

  for (let index = start + 1; index < lines.length; index += 1) {
    const text = lineText(lines[index]);
    const normalized = normalizeText(text);

    if (/^(observacoes|referencias metodologicas)/.test(normalized)) break;

    const match = text.match(RESULT_LINE);

    if (match) {
      results.push({
        name: match[2].trim(),
        key: analysisKey(match[2]),
        value: parseBrazilianNumber(match[3]),
        unit: (match[4] || '').replace(/[μu]m/i, 'µm'),
      });
    }
  }

  return results;
}

export function parseLabReport(pages: PdfPage[]): LabReport {
  const lines = pages.flatMap(page => groupLines(page));
  const fields = readLabeledFields(pages[0] ? groupLines(pages[0]) : []);
  const all = lines.map(lineText);
  const codeLine = all.find(text => /c[oó]digo da amostra\s*:/i.test(text));
  const sampleCode =
    codeLine?.split(':').slice(1).join(':').trim().split(/\s+/)[0] || null;
  const labLine = all.find(
    text => /laborat[oó]rio/i.test(text) && / - /.test(text),
  );
  const results = readResults(lines);
  const values: Partial<Record<NutrientKey, number>> = {};

  results.forEach(result => {
    if (
      result.key &&
      result.value !== null &&
      values[result.key] === undefined
    ) {
      values[result.key] = result.value;
    }
  });

  const report: LabReport = {
    sample_code: sampleCode,
    item_name: blankToNull(fields.item),
    lot: blankToNull(fields.lot),
    supplier: blankToNull(fields.supplier),
    manufactured_on: parseDate(fields.manufactured),
    collected_on: parseDate(fields.collected),
    received_on: parseDate(fields.received),
    released_at: parseDateTime(fields.released),
    // "Aurora Coop - Laboratório de ... - Rua ..." -> até antes do endereço
    laboratory: labLine
      ? labLine.split(' - ').slice(0, 2).join(' - ').trim()
      : null,
    results,
    values,
    missing: [],
  };

  if (!report.sample_code) report.missing.push('sample_code');
  if (!report.item_name) report.missing.push('item_name');
  if (!report.collected_on) report.missing.push('collected_on');
  if (!Object.keys(values).length) report.missing.push('results');

  return report;
}
